import { ChangeEvent, FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
	faArrowLeft,
	faChevronRight,
	faCircleInfo,
	faFolderPlus,
	faList,
} from "@fortawesome/pro-regular-svg-icons";

import { Modal } from "@/components";
import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";
import { useGetCampaignListPickerQuery } from "@/pages/Private/redux/list/campaigns-overview.api";
import { ROUTE_CONFIG } from "@/routes/config";
import { classNames } from "@/utils/classNames";
import { useSearch } from "@/utils";

type Step = "choice" | "picker";

/**
 * New-Campaign entry modal of the Campaigns overview (PREC-3880). A campaign always belongs to
 * one lead list, so the modal only routes into the existing flows: pick an existing list → its
 * copywriting builder with `?createCampaign=true` (the same deep link the List overview uses),
 * or start a new list in the New List builder.
 */
export const NewCampaignModal: FunctionComponent<{ isOpen: boolean; onClose: () => void }> = ({
	isOpen,
	onClose,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [step, setStep] = useState<Step>("choice");
	const [selectedListId, setSelectedListId] = useState<number | null>(null);
	const { searchValue, setSearchValue, query } = useSearch();

	const { data } = useGetCampaignListPickerQuery(
		{ searchValue: query || undefined },
		{ skip: !isOpen || step !== "picker" }
	);
	const lists = data?.data ?? [];
	const selectedList = lists.find((list) => list.id === selectedListId);

	const close = () => {
		setStep("choice");
		setSelectedListId(null);
		setSearchValue("");
		onClose();
	};

	const goTo = (path: string) => {
		close();
		navigate(path);
	};

	if (!isOpen) {
		return null;
	}

	const note = (
		<div className="mb-4 flex gap-2.5 rounded-lg bg-brand-white p-3 text-sm text-text">
			<Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand-light" icon={faCircleInfo} />
			<span>
				{step === "choice"
					? t("campaignsOverview.newCampaignModal.note")
					: t("campaignsOverview.newCampaignModal.pickerNote")}
			</span>
		</div>
	);

	if (step === "choice") {
		return (
			<Modal
				handleClose={close}
				isOpened={isOpen}
				size="sm"
				title={t("campaignsOverview.newCampaignModal.title")}
			>
				{note}
				<button
					className="mb-2.5 flex w-full cursor-pointer items-center gap-3 rounded-xl border border-border-light bg-white p-3.5 text-left hover:border-brand-light hover:shadow-sm"
					type="button"
					onClick={() => setStep("picker")}
				>
					<span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-white text-brand-light">
						<Icon className="h-4 w-4" icon={faList} />
					</span>
					<span className="min-w-0 flex-1">
						<span className="block text-sm font-semibold">
							{t("campaignsOverview.newCampaignModal.cardExisting.title")}
						</span>
						<span className="block text-xs text-text-meta">
							{t("campaignsOverview.newCampaignModal.cardExisting.description")}
						</span>
					</span>
					<Icon className="h-3.5 w-3.5 flex-shrink-0 text-text-meta" icon={faChevronRight} />
				</button>
				<button
					className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-border-light bg-white p-3.5 text-left hover:border-brand-light hover:shadow-sm"
					type="button"
					onClick={() => goTo(`${ROUTE_CONFIG.LISTS}/create`)}
				>
					<span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-white text-brand-light">
						<Icon className="h-4 w-4" icon={faFolderPlus} />
					</span>
					<span className="min-w-0 flex-1">
						<span className="block text-sm font-semibold">
							{t("campaignsOverview.newCampaignModal.cardNewList.title")}
						</span>
						<span className="block text-xs text-text-meta">
							{t("campaignsOverview.newCampaignModal.cardNewList.description")}
						</span>
					</span>
					<Icon className="h-3.5 w-3.5 flex-shrink-0 text-text-meta" icon={faChevronRight} />
				</button>
			</Modal>
		);
	}

	return (
		<Modal
			disableSave={!selectedList}
			handleClose={close}
			handleSave={() =>
				selectedList &&
				goTo(`${ROUTE_CONFIG.LISTS}/create/copywriting/${selectedList.id}?createCampaign=true`)
			}
			isOpened={isOpen}
			size="sm"
			submitButtonText={t("campaignsOverview.newCampaignModal.continue")}
			title={t("campaignsOverview.newCampaignModal.pickerTitle")}
		>
			<button
				className="mb-3 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-text-meta hover:text-text"
				type="button"
				onClick={() => setStep("choice")}
			>
				<Icon className="h-3 w-3" icon={faArrowLeft} />
				{t("basics.back")}
			</button>
			{note}
			<div className="rounded-lg border border-border-light">
				<div className="border-b border-border-light p-2">
					<InputField
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
						name="pickerSearch"
						placeholder={t("campaignsOverview.newCampaignModal.searchPlaceholder")}
						value={searchValue}
					/>
				</div>
				<div className="max-h-[240px] overflow-y-auto p-1.5">
					{lists.length === 0 && (
						<div className="p-3 text-sm text-text-meta">
							{t("campaignsOverview.newCampaignModal.noneFound")}
						</div>
					)}
					{lists.map((list) => (
						<button
							key={list.id}
							className={classNames(
								"flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-gray-50",
								selectedListId === list.id ? "bg-brand-white hover:bg-brand-white" : ""
							)}
							type="button"
							onClick={() => setSelectedListId(list.id)}
						>
							<span className="min-w-0 flex-1 truncate text-sm font-medium">{list.name}</span>
							<span className="flex-shrink-0 whitespace-nowrap text-xs text-text-meta">
								{t("campaignsOverview.newCampaignModal.meta", {
									prospects: Number(list.assignedLeads ?? 0),
									count: Number(list.campaignCount ?? 0),
								})}
							</span>
						</button>
					))}
				</div>
			</div>
			<div className="mt-3 text-xs text-text-meta">
				{selectedList
					? t("campaignsOverview.newCampaignModal.hintSelected", { name: selectedList.name })
					: t("campaignsOverview.newCampaignModal.hintNone")}
			</div>
		</Modal>
	);
};
